import React, {useState} from 'react';
import {Box, Button, Group, Radio, Stack, Text, TextInput} from '@mantine/core';
import {useQuery, useQueryClient} from '@tanstack/react-query';
import {closeModal, openModal} from '@mantine/modals';
import {notifications} from '@mantine/notifications';
import {startAuthentication, type PublicKeyCredentialRequestOptionsJSON} from '@simplewebauthn/browser';
import {
    ApiError,
    PasskeyService,
    RegistrationMailService,
    UserService,
    type RecipientFilter,
} from '../../client';
import {formatRequestError} from '../../utils/formatRequestError';
import {
    largeEditorModalBodyStyle,
    largeEditorModalProps,
    largeEditorModalScrollAreaStyle,
} from '../../utils/largeEditorModal';
import {RegistrationMailEditor} from './RegistrationMailEditor';

const passkeySupported = typeof window !== 'undefined' && !!window.PublicKeyCredential;

type SendRegistrationMailConfirmationProps = {
    username: string;
    hasPasskey: boolean;
    recipientFilter: RecipientFilter;
};

function SendRegistrationMailConfirmation({
    username,
    hasPasskey,
    recipientFilter,
}: SendRegistrationMailConfirmationProps) {
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const queryClient = useQueryClient();

    const finishSend = () => RegistrationMailService.sendRegistrationMailApiV1RegistrationMailSendPost({
        requestBody: {recipient_filter: recipientFilter},
    })
        .then(() => {
            queryClient.invalidateQueries({queryKey: ['registration-mail-send-status']});
            notifications.show({
                color: 'green',
                title: 'Registration mail sent',
                message: 'The mail is being sent to the selected registrations.',
            });
            closeModal('sendRegistrationMail');
            closeModal('sendRegistrationMailEditor');
        })
        .catch((error: unknown) => {
            notifications.show({
                color: error instanceof ApiError && error.status === 409 ? 'yellow' : 'red',
                title: error instanceof ApiError && error.status === 409
                    ? 'Send already in progress'
                    : 'Could not send mail',
                message: formatRequestError(error, 'Request failed'),
            });
        });

    const sendWithPassword = () => {
        if (!username || !password) {
            return;
        }
        setLoading(true);
        UserService.loginForAccessTokenApiV1UserLoginPost({
            formData: {username, password},
        })
            .then(() => finishSend())
            .catch((error: unknown) => {
                if (error instanceof ApiError && error.status === 409) {
                    return;
                }
                notifications.show({
                    color: 'red',
                    title: 'Could not send mail',
                    message: formatRequestError(error, 'Request failed'),
                });
            })
            .finally(() => setLoading(false));
    };

    const sendWithPasskey = async () => {
        if (!username) {
            return;
        }
        if (!passkeySupported) {
            notifications.show({
                color: 'yellow',
                title: 'Passkey unavailable',
                message: 'Passkeys require HTTPS (e.g. mkcert for local development).',
            });
            return;
        }
        setLoading(true);
        try {
            const optionsResponse = await PasskeyService.passkeyLoginOptionsApiV1PasskeyLoginOptionsPost({
                username: username.trim(),
            });
            if (!optionsResponse.has_passkey || !optionsResponse.options || !optionsResponse.challenge_token) {
                notifications.show({
                    color: 'red',
                    title: 'Could not send mail',
                    message: 'No passkey registered for this user.',
                });
                return;
            }
            const credential = await startAuthentication({
                optionsJSON: optionsResponse.options as unknown as PublicKeyCredentialRequestOptionsJSON,
            });
            await PasskeyService.passkeyLoginVerifyApiV1PasskeyLoginVerifyPost({
                credential: credential as unknown as Record<string, unknown>,
                challenge_token: optionsResponse.challenge_token,
                username: username.trim(),
            });
            await finishSend();
        } catch (error: unknown) {
            notifications.show({
                color: 'red',
                title: 'Could not send mail',
                message: formatRequestError(error, 'Authentication was cancelled or failed.'),
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <Stack gap="xs">
            <Text size="sm">
                This will send the saved mail to the selected registrations.{' '}
                {hasPasskey
                    ? 'Confirm with your passkey to continue.'
                    : 'Enter your password to confirm.'}
            </Text>
            {hasPasskey ? (
                <>
                    {!passkeySupported ? (
                        <Text size="sm" c="dimmed">
                            Passkeys require HTTPS (e.g. mkcert for local development).
                        </Text>
                    ) : null}
                    <Group gap="xs">
                        <Button
                            loading={loading}
                            disabled={!passkeySupported}
                            onClick={() => void sendWithPasskey()}
                        >
                            Confirm with passkey
                        </Button>
                        <Button variant="default" onClick={() => closeModal('sendRegistrationMail')}>
                            Cancel
                        </Button>
                    </Group>
                </>
            ) : (
                <>
                    <TextInput
                        label="Password"
                        type="password"
                        required
                        placeholder="Your password"
                        value={password}
                        onChange={(e) => setPassword(e.currentTarget.value)}
                    />
                    <Group gap="xs">
                        <Button loading={loading} onClick={sendWithPassword}>Send mail</Button>
                        <Button variant="default" onClick={() => closeModal('sendRegistrationMail')}>
                            Cancel
                        </Button>
                    </Group>
                </>
            )}
        </Stack>
    );
}

type RegistrationMailSendModalProps = {
    username: string;
    hasPasskey: boolean;
    sendInProgress: boolean;
};

export function RegistrationMailSendModal({
    username,
    hasPasskey,
    sendInProgress,
}: RegistrationMailSendModalProps) {
    const [recipientFilter, setRecipientFilter] = useState<RecipientFilter>('all');

    const {data: recipientCount, isLoading: recipientCountLoading} = useQuery({
        queryKey: ['registration-mail-recipient-count', recipientFilter],
        queryFn: () => RegistrationMailService.getRegistrationMailRecipientCountApiV1RegistrationMailRecipientCountGet({
            recipientFilter,
        }),
    });

    const count = recipientCount?.count;
    const sendLabel = count === undefined
        ? 'Send mail'
        : count === 1
            ? 'Send mail to 1 recipient'
            : `Send mail to ${count} recipients`;

    const showConfirmationModal = () => {
        if (sendInProgress) {
            notifications.show({
                color: 'yellow',
                title: 'Send already in progress',
                message: 'A registration mail is currently being sent.',
            });
            return;
        }
        openModal({
            modalId: 'sendRegistrationMail',
            title: 'Send registration mail',
            children: (
                <SendRegistrationMailConfirmation
                    username={username}
                    hasPasskey={hasPasskey}
                    recipientFilter={recipientFilter}
                />
            ),
        });
    };

    return (
        <Stack gap="md" style={largeEditorModalBodyStyle}>
            <Box style={largeEditorModalScrollAreaStyle}>
                <Stack gap="md">
                    <RegistrationMailEditor />
                    <Stack gap="xs">
                        <Text size="sm" fw={600}>Recipients</Text>
                        <Radio.Group
                            value={recipientFilter}
                            onChange={(value) => setRecipientFilter(value as RecipientFilter)}
                        >
                            <Stack gap="xs">
                                <Radio value="all" label="All registrations" />
                                <Radio value="participants_only" label="Participants only (exclude staff)" />
                                <Radio value="staff_only" label="Staff only" />
                                <Radio value="unpaid_only" label="Unpaid only (outstanding and delayed)" />
                            </Stack>
                        </Radio.Group>
                    </Stack>
                </Stack>
            </Box>
            <Group gap="xs" justify="flex-end">
                <Button variant="default" onClick={() => closeModal('sendRegistrationMailEditor')}>
                    Cancel
                </Button>
                <Button
                    color="red"
                    variant="outline"
                    onClick={showConfirmationModal}
                    disabled={sendInProgress || recipientCountLoading || count === 0 || count === undefined}
                >
                    {sendLabel}
                </Button>
            </Group>
        </Stack>
    );
}

export function openRegistrationMailSendModal({
    username,
    hasPasskey,
    sendInProgress,
}: RegistrationMailSendModalProps) {
    if (sendInProgress) {
        notifications.show({
            color: 'yellow',
            title: 'Send already in progress',
            message: 'A registration mail is currently being sent.',
        });
        return;
    }
    openModal({
        modalId: 'sendRegistrationMailEditor',
        title: 'Email all registrations',
        ...largeEditorModalProps,
        children: (
            <RegistrationMailSendModal
                username={username}
                hasPasskey={hasPasskey}
                sendInProgress={sendInProgress}
            />
        ),
    });
}
